import React, { useState, useEffect } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import CircularProgress from "@mui/material/CircularProgress";
import Chip from "@mui/material/Chip";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import SyncIcon from "@mui/icons-material/Sync";
import { toast } from "react-toastify";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const UNIDADES = ["UN", "PC", "CX", "FL", "KG", "G", "L", "ML", "M", "M²", "H", "SV"];
const EMPTY_TIER = { minQty: "", maxQty: "", price: "" };
const EMPTY_FORM = {
  serviceCode: "", serviceName: "", category: "Impressão",
  ncm: "", unidade: "FL", active: true,
  tiers: [{ ...EMPTY_TIER }],
  urgencyTiers: [],
};

export default function PricingTableDialog({ open, onClose, table, onSaved }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    if (open) {
      if (table) {
        setForm({
          serviceCode: table.serviceCode || "",
          serviceName: table.serviceName || "",
          category: table.category || "Impressão",
          ncm: table.ncm || "",
          unidade: table.unidade || "FL",
          active: table.active !== false,
          tiers: table.tiers?.length ? table.tiers.map(t => ({
            minQty: t.minQty, maxQty: t.maxQty ?? "", price: t.price
          })) : [{ ...EMPTY_TIER }],
          urgencyTiers: table.urgencyTiers || [],
        });
      } else {
        setForm({ ...EMPTY_FORM, tiers: [{ ...EMPTY_TIER }] });
      }
    }
  }, [open, table]);

  const setField = (field) => (e) => setForm(f => ({ ...f, [field]: e.target.value }));

  const setTierField = (idx, field) => (e) => {
    setForm(f => {
      const tiers = [...f.tiers];
      tiers[idx] = { ...tiers[idx], [field]: e.target.value };
      return { ...f, tiers };
    });
  };

  const addTier = () => setForm(f => ({ ...f, tiers: [...f.tiers, { ...EMPTY_TIER }] }));
  const removeTier = (idx) => setForm(f => ({ ...f, tiers: f.tiers.filter((_, i) => i !== idx) }));

  const buildPayload = () => ({
    ...form,
    tiers: form.tiers
      .filter(t => t.minQty !== "" && t.price !== "")
      .map(t => ({
        minQty: Number(t.minQty),
        maxQty: t.maxQty === "" ? null : Number(t.maxQty),
        price: parseFloat(t.price),
      })),
  });

  const handleSave = async () => {
    if (!form.serviceName.trim()) { toast.warn("Informe o nome do serviço."); return; }
    if (!form.serviceCode.trim()) { toast.warn("Informe o código do serviço."); return; }
    setSaving(true);
    try {
      if (table?.id) {
        await api.put(`/catalog/pricing-tables/${table.id}`, buildPayload());
        toast.success("Serviço atualizado.");
      } else {
        await api.post("/catalog/pricing-tables", buildPayload());
        toast.success("Serviço criado.");
      }
      onSaved?.();
      onClose();
    } catch (err) { toastError(err); }
    finally { setSaving(false); }
  };

  const handleSyncBling = async () => {
    if (!table?.id) { toast.warn("Salve o serviço antes de sincronizar."); return; }
    setSyncing(true);
    try {
      const { data } = await api.post(`/catalog/pricing-tables/${table.id}/sync-bling`);
      toast.success(`Sincronizado com Bling (ID ${data.blingProductId}).`);
      onSaved?.();
    } catch (err) { toastError(err); }
    finally { setSyncing(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          {table?.id ? "Editar Serviço" : "Novo Serviço com Tabela de Preço"}
          {table?.blingProductId && (
            <Chip label={`● Bling ID ${table.blingProductId}`} size="small" color="success" variant="outlined" />
          )}
        </Box>
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={8}>
            <TextField label="Nome do Serviço *" fullWidth value={form.serviceName} onChange={setField("serviceName")} />
          </Grid>
          <Grid item xs={4}>
            <TextField label="Código *" fullWidth value={form.serviceCode} onChange={setField("serviceCode")}
              disabled={!!table?.id} helperText={table?.id ? "Imutável após criação" : ""} />
          </Grid>
          <Grid item xs={4}>
            <TextField label="Categoria" fullWidth value={form.category} onChange={setField("category")} />
          </Grid>
          <Grid item xs={4}>
            <TextField label="NCM" fullWidth value={form.ncm} onChange={setField("ncm")} />
          </Grid>
          <Grid item xs={2}>
            <TextField select label="Unidade" fullWidth value={form.unidade} onChange={setField("unidade")}>
              {UNIDADES.map(u => <MenuItem key={u} value={u}>{u}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={2} sx={{ display: "flex", alignItems: "center" }}>
            <FormControlLabel
              control={<Switch checked={form.active} onChange={e => setForm(f => ({ ...f, active: e.target.checked }))} />}
              label="Ativo"
            />
          </Grid>
        </Grid>

        <Typography variant="subtitle2" sx={{ mt: 3, mb: 1, fontWeight: 700 }}>
          Faixas de Preço
        </Typography>
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 40px", gap: 1, mb: 1 }}>
          <Typography variant="caption" color="text.secondary">De (qtd)</Typography>
          <Typography variant="caption" color="text.secondary">Até (qtd) — vazio = sem limite</Typography>
          <Typography variant="caption" color="text.secondary">Preço / {form.unidade} (R$)</Typography>
          <span />
        </Box>
        {form.tiers.map((t, idx) => (
          <Box key={idx} sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 40px", gap: 1, mb: 1 }}>
            <TextField size="small" type="number" placeholder="1" value={t.minQty} onChange={setTierField(idx, "minQty")} />
            <TextField size="small" type="number" placeholder="∞" value={t.maxQty} onChange={setTierField(idx, "maxQty")} />
            <TextField size="small" type="number" placeholder="0,00" value={t.price} onChange={setTierField(idx, "price")} />
            <IconButton size="small" onClick={() => removeTier(idx)} disabled={form.tiers.length === 1}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        ))}
        <Button size="small" startIcon={<AddIcon />} onClick={addTier} sx={{ mt: 1 }}>
          Adicionar faixa
        </Button>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        {table?.id && (
          <Button
            variant="outlined"
            startIcon={syncing ? <CircularProgress size={14} /> : <SyncIcon />}
            onClick={handleSyncBling}
            disabled={syncing}
          >
            Sincronizar com Bling
          </Button>
        )}
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose}>Cancelar</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? <CircularProgress size={18} /> : "Salvar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
